import { useState, useCallback, useEffect } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useAppConfig } from '../../hooks/useAppConfig';

interface IUserIdleProps {
  /** Is the user authenticated */
  isAuthenticated: boolean;

  /** Called anytime a user is interacting with the site */
  onUserInteraction: () => void;
}

/**
 *  Monitor User idleness and Auto Log the user out of their session if goes too long
 */
export const useUserIdle = ({
  isAuthenticated,
  onUserInteraction,
}: IUserIdleProps) => {
  const [showIdlePrompt, setShowIdlePrompt] = useState(false);
  const [isUserIdleLogout, setIsUserIdleLogout] = useState(false);

  const {
    auth: {
      autoLogout: { timeoutMins, preTimeoutWarnMins },
    },
  } = useAppConfig();

  const { start: startIdleTimer, pause: stopIdleTimer } = useIdleTimer({
    // Total time before auto logout or session expiration
    timeout: timeoutMins * 60 * 1000,

    // Time before timeout for prompt to appear
    promptBeforeIdle: preTimeoutWarnMins * 60 * 1000,

    onPrompt: () => setShowIdlePrompt(true),

    onIdle: () => setIsUserIdleLogout(true),

    // Anytime the user interacts with the
    eventsThrottle: 2000, // give at least 2 secs between `onAction` calls
    onAction: onUserInteraction,

    startManually: true,
  });

  /** Reset the idle timer and prompt */
  const resetIdlePrompt = useCallback(() => {
    setShowIdlePrompt(false);
    startIdleTimer();
    setIsUserIdleLogout(false);
  }, [startIdleTimer]);

  useEffect(() => {
    if (isAuthenticated) {
      // Start monitoring user inactivity
      startIdleTimer();
      return;
    }
    stopIdleTimer();
  }, [isAuthenticated, startIdleTimer, stopIdleTimer]);

  return {
    isUserIdleLogout,
    startIdleTimer,
    stopIdleTimer,
    showIdlePrompt,
    resetIdlePrompt,
  };
};
